import { Download01 } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

import type { LatestRelease } from '../use-latest-release';

const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: 'Install and sign in',
    body: 'Download the app, open it, and sign in with your Ahoy account.',
  },
  {
    step: 2,
    title: 'Grant microphone access',
    body: 'Ahoy Desktop captures audio locally – no bot joins your call.',
  },
  {
    step: 3,
    title: 'Start your meeting',
    body: 'Notes arrive in Ahoy when the meeting ends.',
  },
] as const;

const REQUIREMENTS = [
  'Apple Silicon Mac (M1 or later)',
  'macOS 13 Ventura or later',
  'Microphone permission',
] as const;

const SECURITY_POINTS = [
  'No bot joins your meeting – recording is device-local.',
  'Audio is sent directly to our recording partner for transcription.',
  'You must be signed in to Ahoy to use the app.',
] as const;

const HOW_SECTION_TITLE_ID = 'desktop-how-it-works-title';
const REQUIREMENTS_SECTION_TITLE_ID = 'desktop-requirements-title';
const SECURITY_SECTION_TITLE_ID = 'desktop-security-title';
const DOWNLOAD_DESC_ID = 'desktop-download-desc';

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ReadyToDownloadProps {
  release: LatestRelease;
}

export function ReadyToDownload({ release }: ReadyToDownloadProps) {
  return (
    <div className='flex flex-col gap-12'>
      <div className='flex flex-col items-center gap-6 text-center'>
        <p className='text-xl text-[var(--color-text-secondary)]'>
          No bots in your meetings. Just clean notes.
        </p>
        <div className='flex flex-col items-center gap-3'>
          <span className='sr-only' id={DOWNLOAD_DESC_ID}>
            Downloads the .dmg installer for Apple Silicon Macs
          </span>
          <Button
            aria-describedby={DOWNLOAD_DESC_ID}
            color='primary'
            download
            href={release.download_url ?? undefined}
            iconLeading={Download01}
            size='lg'
            disabled={!release.download_url}
          >
            Download for macOS
          </Button>
          <p className='text-sm text-[var(--color-text-secondary)]'>
            {[
              release.version,
              release.published_at
                ? `Released ${formatRelativeTime(release.published_at)}`
                : null,
              release.size != null ? formatBytes(release.size) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </div>

      <section
        aria-labelledby={HOW_SECTION_TITLE_ID}
        className='flex flex-col gap-4'
      >
        <h2
          className='text-lg font-semibold text-[var(--color-text-primary)]'
          id={HOW_SECTION_TITLE_ID}
        >
          How it works
        </h2>
        <ol className='flex flex-col gap-4'>
          {HOW_IT_WORKS_STEPS.map(({ step, title, body }) => (
            <li className='flex gap-4' key={step}>
              <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-600)] text-sm font-semibold text-white'>
                {step}
              </span>
              <div>
                <p className='font-medium text-[var(--color-text-primary)]'>
                  {title}
                </p>
                <p className='mt-0.5 text-sm text-[var(--color-text-secondary)]'>
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section
        aria-labelledby={REQUIREMENTS_SECTION_TITLE_ID}
        className='flex flex-col gap-4'
      >
        <h2
          className='text-lg font-semibold text-[var(--color-text-primary)]'
          id={REQUIREMENTS_SECTION_TITLE_ID}
        >
          Requirements
        </h2>
        <ul className='flex flex-col gap-2 text-sm text-[var(--color-text-secondary)]'>
          {REQUIREMENTS.map(req => (
            <li className='flex items-center gap-2' key={req}>
              <span className='h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--color-brand-600)]' />
              {req}
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby={SECURITY_SECTION_TITLE_ID}
        className='flex flex-col gap-4'
      >
        <h2
          className='text-lg font-semibold text-[var(--color-text-primary)]'
          id={SECURITY_SECTION_TITLE_ID}
        >
          Security
        </h2>
        <ul className='flex flex-col gap-2 text-sm text-[var(--color-text-secondary)]'>
          {SECURITY_POINTS.map(point => (
            <li className='flex items-center gap-2' key={point}>
              <span className='h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--color-brand-600)]' />
              {point}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
